import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, Link } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Settings, LogOut, User, ChevronUp, ChevronLeft, Home, Menu, X, Moon, Sun, MonitorCog } from 'lucide-react'
import { Icon } from '@/components/ui/icon'
import { StorageService } from '@/services/core/storage'
import { AuthService } from '@/services/core/auth'
import { useCurrentUser } from '../contexts/UserContext'
import { useCreateDialog } from '../contexts/CreateDialogContext'
import { useNetwork } from '../contexts/NetworkContext'
import { useNetworkProfile } from '../contexts/NetworkProfileContext'
import { useTheme } from '../contexts/ThemeContext'
import { useModule } from '../contexts/ModuleContext'
import { ModuleSwitcher } from './ModuleSwitcher'
import { NetworkSwitcher } from './NetworkSwitcher'
import { GlobalSearch } from './GlobalSearch'
import { AppVersion } from './AppVersion'
import { EnvironmentCard } from './EnvironmentCard'
import { SupportBar } from './SupportBar'
import { WhatsNew } from './WhatsNew'
import { NotificationsMenu } from './NotificationsMenu'
import { useLogout } from '@/hooks/useLogout'
import { useSupportAccess } from '@/hooks/useSupportAccess'
import { uiiUrlForLocation } from '@/services/core/crossAppUrl'
import { RecentObjects, versionHistoryTargetForPath } from '@/services/shared/versionHistoryApi'
import '../styles/shell.css'

interface AppShellProps {
  children: React.ReactNode
  crumbs?: Array<{ label: string; href?: string }>
  showBack?: boolean
  /** The kind of object the page shows, put in front of its name in the browser title. */
  objectType?: string
}

// What index.html names the app, for pages that name nothing.
const DEFAULT_TITLE = document.title

export function AppShell(props: AppShellProps) {
  // Inside the create dialog the page's content shows without the shell around it.
  const inCreateDialog = !!useCreateDialog()
  return inCreateDialog ? <>{props.children}</> : <Shell {...props} />
}

function Shell({ children, crumbs = [], showBack = false, objectType }: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const sessionUser = StorageService.getCurrentUser()
  const currentUser = useCurrentUser()
  const { currentModule } = useModule()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const { networkProfile } = useNetworkProfile()
  const roleNames = ((currentNetwork && currentUser?.rolesByNetwork[currentNetwork.tagId]) || [])
    .map((role) => role.name)
    .join(', ')
  const uiLogo = networkProfile?.profile?.adnUiLogo || null  // empty string treated as no logo

  const { t } = useTranslation()

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const isActive = (path: string) => {
    if (location.pathname === path) return true
    // Only prefix-match for deeper paths (not module roots like /political)
    return path.split('/').filter(Boolean).length > 1 && location.pathname.startsWith(path + '/')
  }

  // Masquerading is started from the login screen; the shell only reports it.
  const masqueradeState = StorageService.getMasquerade()
  const showSupportBar = useSupportAccess()

  // The object this page shows, when it shows one.
  const versionHistoryTarget = versionHistoryTargetForPath(location.pathname)

  // Remember the object once its page names it, skipping the loading and error placeholders.
  const objectName = crumbs.at(-1)?.label
  const recentScope = `${currentUser?.username ?? ''}|${currentNetwork?.tagId ?? ''}`
  const recordVisit =
    versionHistoryTarget && objectName && objectName !== t('common.loading') && objectName !== t('common.error')
  useEffect(() => {
    if (!recordVisit || !versionHistoryTarget || !objectName || !currentNetwork) return
    RecentObjects.record(recentScope, { ...versionHistoryTarget, name: objectName })
  }, [recordVisit, versionHistoryTarget?.type, versionHistoryTarget?.id, objectName, recentScope])

  // The browser title says where you are: the page, or the kind of object and its name.
  const pageTitle = objectName
    ? objectType
      ? `${objectType} - ${objectName}`
      : objectName
    : DEFAULT_TITLE
  useEffect(() => {
    document.title = pageTitle
    return () => {
      document.title = DEFAULT_TITLE
    }
  }, [pageTitle])

  const handleLogout = useLogout()

  const handleStopMasquerade = async () => {
    const state = StorageService.getMasquerade()
    if (!state) return
    const original = JSON.parse(state.originalToken)
    StorageService.setMasquerade(null)
    if (original) {
      AuthService.storeToken(original)
    } else {
      handleLogout()
      return
    }
    const { UserApi } = await import('@/services/users/userApi')
    const apiUser = await UserApi.getCurrent()
    queryClient.setQueryData(['currentUser'], apiUser)
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'currentUser' })
    StorageService.setCurrentUser({
      id: `api-${btoa(apiUser.username)}`,
      username: apiUser.username,
      displayName: apiUser.displayName || apiUser.name || apiUser.username,
    })
  }

  return (
    <div className={`app-shell${menuOpen ? ' app-shell--menu-open' : ''}`}>
      {/* ── Mobile overlay ── */}
      {menuOpen && <div className="ad-sidebar-overlay" onClick={closeMenu} />}

      {/* ── Sidebar ── */}
      <aside className="ad-sidebar">
        <a
          className="ad-sidebar__brand"
          href={currentModule.landing}
          onClick={(e) => {
            e.preventDefault()
            navigate(currentModule.landing)
            closeMenu()
          }}
        >
          {!uiLogo && <img src="/logo.svg" alt="" className="ad-sidebar__brand-logo" />}
          <span className="flex min-w-0 flex-col">
            {uiLogo
              ? <img src={uiLogo} alt="" className="ad-sidebar__brand-logo" style={{ maxHeight: 32, width: 'auto', maxWidth: '100%' }} />
              : <span className="ad-sidebar__brand-text">ADNUNTIUS</span>
            }
            {currentNetwork?.marketplacePlatform && (
              <span className="text-[10px] leading-tight tracking-widest text-(--ad-text-muted) uppercase">
                {t('appShell.marketplace')}
              </span>
            )}
          </span>
        </a>

        {currentModule.nav.map((section, i) => (
          <div key={i}>
            {section.title && <div className="ad-sidebar__section">{section.title}</div>}
            <nav className="ad-sidebar__nav">
              {section.items.map((item) => (
                <a
                  key={item.id}
                  className={`ad-sidebar__item${item.href && isActive(item.href) ? ' ad-sidebar__item--active' : ''}`}
                  href={item.href || '#'}
                  onClick={(e) => {
                    e.preventDefault()
                    if (item.href) {
                      navigate(item.href)
                      closeMenu()
                    }
                  }}
                >
                  <Icon name={item.icon} className="size-4" />
                  <span>{item.label}</span>
                  {item.badge && <span className="si-badge">{item.badge}</span>}
                </a>
              ))}
            </nav>
          </div>
        ))}

        <div className="ad-sidebar__user-wrap">
          <EnvironmentCard className="mx-3 mb-3" />
          <AppVersion className="mx-4 mb-3" />
          <div className="relative">
            {userMenuOpen && (
              <>
                <div className="ad-user-menu-overlay" onClick={() => setUserMenuOpen(false)} />
                <div className="ad-user-menu">
                  <button className="ad-user-menu__item" onClick={() => { setUserMenuOpen(false); navigate('/preferences') }}>
                    <Settings className="size-4" />
                    <span>{t('appShell.preferences')}</span>
                  </button>
                  <button className="ad-user-menu__item" onClick={handleLogout}>
                    <LogOut className="size-4" />
                    <span>{t('appShell.signOut')}</span>
                  </button>
                </div>
              </>
            )}
            <div className="ad-sidebar__user" onClick={() => setUserMenuOpen(v => !v)} style={{ cursor: 'pointer' }}>
              <div className="ad-sidebar__avatar">
                <User className="size-4" />
              </div>
              <div className="ad-sidebar__user-meta">
                <span className="ad-sidebar__user-name">
                  {currentUser?.displayName || currentUser?.name || sessionUser?.displayName || '—'}
                </span>
                <span className="ad-sidebar__user-role" title={roleNames || undefined}>
                  {roleNames || currentUser?.username || sessionUser?.username || ''}
                </span>
              </div>
              <ChevronUp className={`size-4 ad-sidebar__cog${userMenuOpen ? ' ad-sidebar__cog--active' : ''}`} />
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="ad-main">
        {showSupportBar && (
          <SupportBar
            username={
              masqueradeState
                ? masqueradeState.supportUsername
                : currentUser?.username || sessionUser?.username
            }
            masqueradeAs={masqueradeState?.targetUsername}
            onStopMasquerade={handleStopMasquerade}
          />
        )}

        <header className="ad-topbar">
          <button
            className="ad-topbar__hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={t('appShell.toggleMenu')}
          >
            {menuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>

          {showBack && (
            <button className="ad-topbar__back" onClick={() => navigate(-1)} aria-label={t('appShell.back')}>
              <ChevronLeft className="size-4" />
            </button>
          )}

          <ModuleSwitcher />

          <span className="ad-topbar__sep ad-hide-mobile" />

          <div className="ad-crumbs ad-hide-mobile">
            <Home className="size-4" />
            {crumbs.map((c, i) => (
              <span key={i} style={{ display: 'contents' }}>
                <span className="ad-crumbs__sep">›</span>
                {i === crumbs.length - 1 ? (
                  <span className="ad-crumbs__current" title={c.label}>{c.label}</span>
                ) : c.href ? (
                  <Link to={c.href} title={c.label}>{c.label}</Link>
                ) : (
                  <span className="ad-crumbs__plain" title={c.label}>{c.label}</span>
                )}
              </span>
            ))}
          </div>

          <div className="ad-topbar__spacer" />

          <a
            className="ad-topbar__iconbtn"
            title={t('appShell.openCurrentPageInAdmin')}
            aria-label={t('appShell.openCurrentPageInAdmin')}
            href={uiiUrlForLocation(location.pathname, location.search, currentModule.id)}
          >
            <MonitorCog className="size-4" />
          </a>

          <WhatsNew />

          {!masqueradeState && <NotificationsMenu />}

          <button
            className="ad-topbar__iconbtn"
            title={theme === 'light' ? t('appShell.switchToDark') : t('appShell.switchToLight')}
            onClick={toggleTheme}
          >
            {theme === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
          </button>

          <NetworkSwitcher />

          <div className="ad-topbar__search">
            <GlobalSearch />
          </div>
        </header>

        {children}
      </div>
    </div>
  )
}
